import React, { useEffect, useState } from "react";
import { Table, Tag, Card, Descriptions } from "antd";
import { getBulkStudents } from "../../../../../api/globalAdmin/Index";
import WebUtils from "../../../../../utils/WebUtils";
import Utils from "../../../../../utils/Utils";

const SingleBulkDetails = ({ setLoading, batchIdentifier, bulkId }) => {
  const userDetail = Utils.getUserDetail();
  const [bulkData, setBulkData] = useState(null);

  const getAllBulks = (pageNo, pageSize, searchInput) => {
    const url = `${getBulkStudents}?organizationId=${
      userDetail?.organizationId || null
    }&batchIdentifier=${batchIdentifier}&bulkIdentifier=${bulkId}`;
    setLoading(true);

    WebUtils.httpOperations(url, {}, "GET")
      .then((result) => {
        setLoading(false);
        if (
          result.data.resultStatusInfo.resultCode.toLowerCase() === "success"
        ) {
          setBulkData(result?.data?.data);
        }
      })
      .catch((err) => {
        setLoading(false);
        console.log(err);
      });
  };

  useEffect(() => {
    getAllBulks();
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case "SUCCESS":
        return "success";
      case "PROCESSING_FAILED":
        return "error";
      case "IN_PROGRESS":
        return "warning";
      default:
        return "default";
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case "SUCCESS":
        return "bg-green-50 text-green-700 border-green-200";
      case "COMPLETED":
        return "bg-green-50 text-green-700 border-green-200";
      case "PROCESSING_FAILED":
        return "bg-red-50 text-red-700 border-red-200";
      case "IN_PROGRESS":
        return "bg-yellow-50 text-yellow-700 border-yellow-200";
      default:
        return "bg-red-50 text-red-700 border-red-200";
    }
  };

  const formatStatus = (status) => {
    if (!status) return "—";

    return status
      .toLowerCase()
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const columns = [
    {
      title: (
        <span className="text-[#475467] font-medium text-[.85rem]">S. No.</span>
      ),
      key: "sno",
      width: 70,
      render: (_, __, index) => (
        <span className="ml-2 text-[#475467]">{index + 1}</span>
      ),
    },
    {
      title: (
        <span className="text-[#475467] font-medium text-[.85rem]">
          Student Name
        </span>
      ),
      key: "name",
      width: 200,
      render: (_, record) => (
        <span className="font-medium text-gray-800">
          {record.firstName} {record.lastName}
        </span>
      ),
    },
    {
      title: (
        <span className="text-[#475467] font-medium text-[.85rem]">Email</span>
      ),
      dataIndex: "emailId",
      key: "emailId",
      width: 200,
      render: (email) => (
        <span className="text-gray-600 text-[.85rem]">{email}</span>
      ),
    },
    {
      title: (
        <span className="text-[#475467] font-medium text-[.85rem]">Status</span>
      ),
      dataIndex: "candidateStatus",
      key: "candidateStatus",
      width: 180,
      align: "center",
      render: (status) => (
        <span
          className={`inline-flex items-center gap-1 rounded-md border px-3 py-1 text-[.85rem] font-medium ${getStatusClass(
            status,
          )}`}
        >
          {formatStatus(status)}
        </span>
      ),
    },
    {
      title: (
        <span className="text-[#475467] font-medium text-[.85rem]">
          Message
        </span>
      ),
      dataIndex: "message",
      key: "message",
      align: "center",
      width: 250,
      render: (message) => (
        <span className="text-gray-500 text-[.85rem]">{message || "—"}</span>
      ),
    },
  ];

  if (!bulkData) {
    return null;
  }

  return (
    <div className="space-y-4 px-5 py-3">
      {/* Bulk Card Component */}
      <Card className="rounded-xl shadow-sm border border-[#E8E2D9]">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <p className="text-sm text-[#0F1B23] font-medium mb-[.35rem]">
              Bulk ID
            </p>
            <p className="text-sm sub-text-color">{bulkData.bulkIdentifier}</p>
          </div>
          <div>
            <p className="text-sm text-[#0F1B23] font-medium mb-[.35rem]">
              Batch ID
            </p>
            <p className="text-sm sub-text-color">{bulkData.batchIdentifier}</p>
          </div>
          <div>
            <p className="text-sm text-gray-900 font-medium mb-[.35rem]">
              Bulk Status
            </p>
            <span
              className={`inline-flex items-center gap-1 rounded-md border px-3 py-1 text-[.85rem] font-medium ${getStatusClass(
                bulkData?.bulkStatus,
              )}`}
            >
              {formatStatus(bulkData?.bulkStatus)}
            </span>
          </div>
          <div>
            <p className="text-sm text-[#0F1B23] font-medium mb-[.35rem]">
              Created On
            </p>
            <p className="text-sm sub-text-color">
              {new Date(bulkData.creationDate).toLocaleString("en-IN", {
                day: "2-digit",
                month: "short",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })}
            </p>
          </div>

          {/* <div className="md:col-span-2 lg:col-span-4">
            <p className="text-sm text-[#0F1B23] mb-1 font-medium">Message</p>
            <p className="text-sm sub-text-color font-medium bg-gray-50 p-2 rounded-lg border-[1px] border-gray-200">
              {bulkData.messages || "—"}
            </p>
          </div> */}
        </div>
      </Card>

      {/* Student Table Component */}
      <Card className="rounded-xl shadow-sm border border-[#E8E2D9]">
        <div className="text-base mb-4 font-semibold flex gap-3 items-center">
          Bulk Students{" "}
          <span className="px-3 flex gap-2 items-center py-1 bg-yellow-50 font-medium text-yellow-700 shadow-sm border border-yellow-300 rounded-full text-[.88rem]">
            {bulkData?.bulkB2BCandidateDataList?.length || 0} students
          </span>
        </div>
        <Table
          columns={columns}
          dataSource={bulkData.bulkB2BCandidateDataList}
          rowKey="emailId"
          pagination={false}
          // pagination={{
          //   pageSize: 10,
          //   showSizeChanger: true,
          //   showQuickJumper: true,
          // }}
          scroll={{ x: 1000 }}
        />
      </Card>
    </div>
  );
};

export default SingleBulkDetails;
